import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssetSearchModal } from '~/components/asset-search-modal/AssetSearchModal';
import { QWSStatusView } from '~/components/qws-status-view/QWSStatusView';
import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { WATCHLIST_ID } from '~/config/constants';
import { useFilterActions } from '~/hooks/use-filter-actions/useFilterActions';
import { ModalTypes, useModal } from '~/hooks/use-modal/useModal';
import { useQWSCodelist } from '~/hooks/use-qws-codelist/useQWSCodelist';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useAssetsFilterEntityTags } from '~/react-query-hooks/useAssetsFilterEntityTags';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { ScenarioAssistantModal } from '~/widgets/scenario/components/scenario-assistant-modal/ScenarioAssistantModal';
import { useScenarioAssistant } from '~/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { ScenarioConditions } from '~/widgets/scenario/components/scenario-conditions/ScenarioConditions';
import { useScenarioStore } from '~/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { SCENARIO_ASSET_FILTER_KEY } from '~/widgets/scenario/hooks/use-scenario-store/filter-utils';

import { ClearAllButton } from './header/clear-all-button/ClearAllButton';
import { useSetScenarioWorkingList } from './hooks/useSetScenarioWorkingList';
import styles from './Scenario.module.scss';

export const Scenario = () => {
  const { t } = useTranslation([
    'widget',
    'chart',
    'scenario',
    'screener',
    'scenarioCommander',
    'countries',
    'analyze',
    'common',
    'qws',
  ]);

  const { params, deleteQueryParam } = useQueryParams();
  const {
    status: qwsStatus,
    watchlistId: qwsWatchlistId,
    error: qwsError,
  } = useQWSCodelist();
  const { setFilterOptions } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });

  const addConditionModal = useModal(ModalTypes.AddCondition);
  const addAssetsModal = useModal(ModalTypes.AddAssets);

  const [scenarioAssistantConditionId, setScenarioAssistantConditionId] =
    useState<string | undefined>();

  const {
    filterGroups,
    scenarioPageEntityTags,
    scenarioPageSearchResultsLimit,
    setScenarioPageEntityTags,
  } = useScenarioStore(state => ({
    filterGroups: state.filterGroups,
    scenarioPageEntityTags: state.scenarioPageEntityTags,
    scenarioPageSearchResultsLimit: state.scenarioPageSearchResultsLimit,
    setScenarioPageEntityTags: state.setScenarioPageEntityTags,
  }));

  //TODO
  // When redirecting to screener page, the filters from scenario are being applied on screener page
  // Uncomment when fixed bug on redirection to screener page
  // useFiltersQueryParam(useScenarioStore);

  const { data: scenarioFiltersTags } = useAssetsFilterEntityTags(filterGroups);
  const { setWorkingList } = useSetScenarioWorkingList();

  const qwsFilterAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!qwsWatchlistId) {
      qwsFilterAppliedRef.current = null;
      return;
    }

    if (qwsStatus !== 'done') {
      return;
    }

    if (qwsFilterAppliedRef.current === qwsWatchlistId) {
      return;
    }

    qwsFilterAppliedRef.current = qwsWatchlistId;

    setFilterOptions({
      filterKey: SCENARIO_ASSET_FILTER_KEY,
      options: [
        {
          name: t('qws:watchlist.name'),
          key: qwsWatchlistId,
          value: { [WATCHLIST_ID]: qwsWatchlistId },
        },
      ],
    });

    deleteQueryParam(queryKeys.codelist, { replace: true });
  }, [qwsStatus, qwsWatchlistId, setFilterOptions, t, deleteQueryParam]);

  const resetAskedQuestions = useScenarioAssistant(
    state => state.resetAskedQuestions
  );

  useEffect(() => {
    setScenarioPageEntityTags(scenarioFiltersTags);
  }, [JSON.stringify(scenarioFiltersTags)]);

  const changeEditFilter = (conditionId: string) => {
    if (conditionId) {
      setScenarioAssistantConditionId(conditionId);
      addConditionModal.open();
    }
  };

  const handleUpdateItems = (modalTags: string[]) => {
    setWorkingList(modalTags);
  };

  const showQwsStatus =
    qwsStatus === 'loading' ||
    qwsStatus === 'error' ||
    (qwsStatus === 'done' && !!qwsWatchlistId);
  if (showQwsStatus) {
    return (
      <QWSStatusView
        status={qwsStatus}
        watchlistId={qwsWatchlistId}
        error={qwsError}
      />
    );
  }

  return (
    <WithFooterWrapper
      shouldHide={!!params[queryKeys.activeDrawer]}
      trackingMetadata={{ source: POSTHOG_TRACKING_SOURCES.SCENARIO }}
    >
      <div className={styles.root}>
        <div className={styles.headerWrapper}>
          <span className={styles.title}>{t('scenario:scenario')}</span>
          <ClearAllButton />
        </div>
        <ScenarioConditions
          entitiesTags={scenarioPageEntityTags}
          noAssetsButtonLabel={t('scenario:emptyState.addAssets')}
          onEditAssets={() => addAssetsModal.open()}
          onAddConditions={() => {
            resetAskedQuestions();
            addConditionModal.open();
          }}
          onEditAssistantCondition={changeEditFilter}
        />
        {addConditionModal.isOpen && (
          <ScenarioAssistantModal
            editFilterKey={scenarioAssistantConditionId}
            isOpen
            onClose={() => {
              setScenarioAssistantConditionId(undefined);
              addConditionModal.close();
            }}
            resetEditFilterKey={() => {
              setScenarioAssistantConditionId(undefined);
            }}
          />
        )}
        {addAssetsModal.isOpen && (
          <AssetSearchModal
            isOpen
            entityTags={scenarioPageEntityTags}
            pageSearchResultsLimit={scenarioPageSearchResultsLimit}
            onClose={addAssetsModal.close}
            handleUpdateItems={handleUpdateItems}
          />
        )}
      </div>
    </WithFooterWrapper>
  );
};
